import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { LayoutChangeEvent, LayoutRectangle, ViewStyle } from 'react-native';
import { View } from 'react-native';

import type { OverlayContainerDimension } from './use-overlay-container-dimension';
import { useOverlayContainerDimension } from './use-overlay-container-dimension';

type CssLength = `${number}px` | `${number}%` | `${number}em` | `${number}rem` | `${number}vh` | `${number}vw`;
type OverlayDimensionIframe = {
    width?: number | CssLength | null;
    height?: number | CssLength | null;
    minWidth?: number | CssLength | null;
    maxWidth?: number | CssLength | null;
    minHeight?: number | CssLength | null;
    maxHeight?: number | CssLength | null;
};

type OverlayPositionIframe = {
    left?: number | CssLength | null;
    right?: number | CssLength | null;
    top?: number | CssLength | null;
    bottom?: number | CssLength | null;
};

function splitOffPositionStyle(style: ViewStyle = {}) {
    const positionKeys: (keyof ViewStyle)[] = ['top', 'bottom', 'left', 'right'];

    const positionStyle = Object.fromEntries(
        Object.entries(style).filter(([key]) => positionKeys.includes(key as keyof ViewStyle)),
    ) as Pick<ViewStyle, 'top' | 'bottom' | 'left' | 'right'>;

    const rest = Object.fromEntries(Object.entries(style).filter(([key]) => !positionKeys.includes(key as keyof ViewStyle))) as Omit<
        ViewStyle,
        'top' | 'bottom' | 'left' | 'right'
    >;

    return { positionStyle, rest };
}

/** @public */
export function splitOffDimensionStyle(style: ViewStyle = {}) {
    const dimensionKeys: (keyof ViewStyle)[] = ['width', 'height', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight'];

    const dimensionStyle = Object.fromEntries(
        Object.entries(style).filter(([key]) => dimensionKeys.includes(key as keyof ViewStyle)),
    ) as Pick<ViewStyle, 'width' | 'height' | 'minWidth' | 'maxWidth' | 'minHeight' | 'maxHeight'>;

    const rest = Object.fromEntries(Object.entries(style).filter(([key]) => !dimensionKeys.includes(key as keyof ViewStyle))) as Omit<
        ViewStyle,
        'width' | 'height' | 'minWidth' | 'maxWidth' | 'minHeight' | 'maxHeight'
    >;

    return { dimensionStyle, rest };
}

/** @public */
export function toggleOverlayVisibility(visible: boolean) {
    window.parent.postMessage({ type: 'psi-change-video-overlay-visibility', visible }, '*');
}

/** @public */
export function toggleOverlayPointerEvents(value: boolean) {
    window.parent.postMessage({ type: 'psi-change-video-overlay-pointer-events', value }, '*');
}

/** @public */
export function toggleOverlayBackdropVisibility(value: boolean) {
    window.parent.postMessage({ type: 'psi-change-video-overlay-backdrop-visibility', value }, '*');
}

function ensurePixelValueForWeb(value: any) {
    if (typeof value === 'number') return `${value}px`;
    return value ?? null;
}

// Overlay Screen The content of this component controls the position and
// dimension of the overlay screen iframe position is to be given as object
// containing left, right, top or bottom When no position is given along an axis
// it will center itself dimension is to be given as object containig width or
// height When no height is specified it takes the automatic height of the
// content When no width is specifiec it will take up all the availble height
// NOTE: This component should ever only exist once, else they will mess each
// other up
function buildDimensionData(dimensionContent: LayoutRectangle): OverlayDimensionIframe {
    const dimData: OverlayDimensionIframe = {};
    if (!dimData.height) dimData.height = dimensionContent.height;
    if (!dimData.width) dimData.width = dimensionContent.width;
    dimData.width = ensurePixelValueForWeb(dimData.width);
    dimData.height = ensurePixelValueForWeb(dimData.height);
    dimData.minWidth = ensurePixelValueForWeb(dimData.minWidth);
    dimData.maxWidth = ensurePixelValueForWeb(dimData.maxWidth);
    dimData.minHeight = ensurePixelValueForWeb(dimData.minHeight);
    dimData.maxHeight = ensurePixelValueForWeb(dimData.maxHeight);
    return dimData;
}

// FIXME: This function is too large and should be broken up

function OverlayScreenIframe({ children, style, visible = true }: OverlayScreenProps) {
    // Keep track of the size of the children
    const [dimensionContent, setDimensionContent] = useState<LayoutRectangle>({ width: 0, height: 0, x: 0, y: 0 });
    // overlayDimension is the size of the video player and the div with the id
    // "psi-overlay-screen-container"
    const overlayDimension = useOverlayContainerDimension();

    const { positionStyle, rest: restPositionStyle } = splitOffPositionStyle(style);
    const { dimensionStyle, rest } = splitOffDimensionStyle(restPositionStyle);

    function onLayout(e: LayoutChangeEvent) {
        // Keep track of the content's dimensions
        setDimensionContent(e.nativeEvent.layout);
    }

    // Memoize the iframe payloads so the effects can list a single stable value
    // as their dep.
    const iframePosition: OverlayPositionIframe = useMemo(
        () => ({
            left: ensurePixelValueForWeb(positionStyle.left),
            right: ensurePixelValueForWeb(positionStyle.right),
            top: ensurePixelValueForWeb(positionStyle.top),
            bottom: ensurePixelValueForWeb(positionStyle.bottom),
        }),
        [positionStyle.left, positionStyle.right, positionStyle.top, positionStyle.bottom],
    );
    const iframeDimension = useMemo(() => buildDimensionData(dimensionContent), [dimensionContent]);

    useEffect(() => {
        // Notify the Iframe when we change the position
        if (!overlayDimension.success) return;
        window.parent.postMessage({ type: 'psi-teaser-position', position: iframePosition }, '*');
    }, [overlayDimension.success, iframePosition]);

    useEffect(() => {
        // Notify the Iframe when we change the dimension or the content changes
        // its dimension
        if (!overlayDimension.success) return;
        window.parent.postMessage({ type: 'psi-teaser-dimension', dimension: iframeDimension }, '*');
    }, [overlayDimension.success, iframeDimension]);

    // Render a View with the iframes dimensions Render an absolute positioned
    // View with the iframes base container size (can be larger than the iframe
    // itself) Inside this, render the content with custom deimension or up to
    // the size of the base container The rendered size will be reflected back
    // to the iframe, matching their dimensions TODO: This does not work well
    // with width. You either have to give it a fixed width or it will always
    // take up the maximum width
    return (
        <View
            style={
                {
                    position: 'relative',
                    width: '100vw',
                    height: '100vh',
                    overflow: 'hidden',
                    opacity: visible && overlayDimension.width ? 1 : 0,
                } as any
            }
        >
            <View
                style={{
                    position: 'absolute',
                    width: overlayDimension.width,
                    height: overlayDimension.height,
                }}
            >
                <View
                    onLayout={onLayout}
                    style={
                        {
                            ...rest,
                            ...dimensionStyle,
                        } as any
                    }
                >
                    {children}
                </View>
            </View>
        </View>
    );
}

function SimplePositionedOverlayScreen({ children, style = {}, visible = true, pointerEvents = true }: OverlayScreenProps) {
    const { positionStyle } = splitOffPositionStyle(style);

    const hasVerticalPosition = positionStyle.top || positionStyle.bottom;
    const hasHorizontalPosition = positionStyle.left || positionStyle.right;

    return (
        <View
            style={{
                position: 'absolute',
                opacity: visible ? 1 : 0,
                pointerEvents: pointerEvents ? 'auto' : 'none',
                left: !hasHorizontalPosition ? '50%' : null,
                top: !hasVerticalPosition ? '50%' : null,
                transform: [{ translateX: !hasHorizontalPosition ? '-50%' : 0 }, { translateY: !hasVerticalPosition ? '-50%' : 0 }],
                ...style,
            }}
        >
            {children}
        </View>
    );
}

type IframeOverlayContainerContextProps = {
    children: React.ReactNode;
    repositionAndResizeIframe?: boolean;
    reflectScreenPropertiesOntoIframe?: boolean;
};

type IframeOverlayContainer = {
    repositionAndResizeIframe: boolean;
    reflectScreenPropertiesOntoIframe: boolean;
    overlayContainerDimension: OverlayContainerDimension;
};
/** @public */
export const IframeOverlayContainerContext = React.createContext<IframeOverlayContainer | undefined>(undefined);

export function OverlayContainerContextProvider({
    children,
    repositionAndResizeIframe = false,
    reflectScreenPropertiesOntoIframe = false,
}: IframeOverlayContainerContextProps) {
    const overlayContainerDimension = useOverlayContainerDimension();

    const overlayContainer = useMemo(
        () => ({
            repositionAndResizeIframe,
            reflectScreenPropertiesOntoIframe,
            overlayContainerDimension,
        }),
        [repositionAndResizeIframe, reflectScreenPropertiesOntoIframe, overlayContainerDimension],
    );
    return <IframeOverlayContainerContext.Provider value={overlayContainer}>{children}</IframeOverlayContainerContext.Provider>;
}
function useIframeOverlayContainer() {
    return useContext(IframeOverlayContainerContext);
}

function useReflectOverlayPropertiesToIframe({ showBackdrop = false, visible = true, pointerEvents = true }: Partial<OverlayScreenProps>) {
    const overlayContainer = useIframeOverlayContainer();

    const communicateWithIframe = overlayContainer?.reflectScreenPropertiesOntoIframe;
    // Reflect the overlay properties back to the iframe
    useEffect(() => {
        if (communicateWithIframe) toggleOverlayVisibility(visible ?? false);
    }, [communicateWithIframe, visible]);

    useEffect(() => {
        if (communicateWithIframe) toggleOverlayPointerEvents(pointerEvents ?? true);
    }, [communicateWithIframe, pointerEvents]);

    useEffect(() => {
        if (communicateWithIframe) toggleOverlayBackdropVisibility(showBackdrop);
    }, [communicateWithIframe, showBackdrop]);
}

export type OverlayScreenProps = {
    children?: React.ReactNode;
    showBackdrop?: boolean;
    visible?: boolean;
    pointerEvents?: boolean;
    style?: ViewStyle;
};
export function OverlayScreen(props: OverlayScreenProps) {
    const overlayContainer = useIframeOverlayContainer();
    if (overlayContainer) {
        return <OverlayScreenInsideIframe {...props} />;
    } else {
        return <SimplePositionedOverlayScreen {...props} />;
    }
}

function OverlayScreenInsideIframe(overlayScreenProps: OverlayScreenProps) {
    const iframeOverlayContainer = useIframeOverlayContainer();
    useReflectOverlayPropertiesToIframe({ ...overlayScreenProps });
    if (iframeOverlayContainer?.repositionAndResizeIframe) {
        if (iframeOverlayContainer.overlayContainerDimension.success) {
            return <OverlayScreenIframe {...overlayScreenProps} />;
        } else {
            return null;
        }
    } else {
        return <SimplePositionedOverlayScreen {...overlayScreenProps} />;
    }
}
